import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IArchivedPatientListItem } from "../../../@types/admin/AdminPatient";
import { TableSorted } from "../../../components/TableSorted";
import {
  ARCHIVED_PATIENTS_QUERY_KEY,
  useArchivedPatients,
} from "../../../hooks/queries/admin/patients";
import { useDebounce } from "../../../hooks/useDebounce";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  EmailLink,
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";
import { HospitalWardFilter } from "./HospitalWardFilter";
import { formatMonitoringLength } from "./patientListFields";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Archived Patients admin page (EN-5171), migrated from the Retool
 * ArchivedPatients app's list screen. Discharged patients; the detail view is
 * the same `/admin/patients/:patientId` route the live list opens.
 *
 * One route for both, deliberately. The two Retool pages had separate
 * PatientDetails screens that diffed to a handful of removed widgets, and the
 * portal's shared `PatientDetailsPanel` decides what to offer from
 * `patient.archived` instead — so an archived patient behaves identically
 * whichever list you reached them from. `GET /Retool/Patients` does not filter
 * archived, so that is a real path, not a hypothetical one.
 *
 * Gated by `patients:access`, not a code of its own (decision 2026-08-27): the
 * live list already exposes every record this page does, so a separate grant
 * would gate a menu item and no data.
 */
export function AdminArchivedPatients() {
  document.title = "Archived patients - Admin";

  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [wardId, setWardId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const { patients, totalCount, isFetching, isError } = useArchivedPatients({
    page,
    pageSize,
    search: debouncedSearch,
    hospitalGroupId: hospitalId ? Number(hospitalId) : undefined,
    wardId: wardId ? Number(wardId) : undefined,
  });

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleHospitalChange = (value: string) => {
    setHospitalId(value);
    setWardId("");
    setPage(1);
  };

  const handleWardChange = (value: string) => {
    setWardId(value);
    setPage(1);
  };

  const columns: ColumnDef<IArchivedPatientListItem, string>[] = useMemo(
    () => [
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (patient) => String(patient.patientId),
        enableSorting: true,
      },
      { accessorKey: "patientName", header: "Name", enableSorting: true },
      {
        accessorKey: "patientEmail",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.patientEmail}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.patientEmail}
          </EmailLink>
        ),
        enableSorting: true,
      },
      { accessorKey: "hospitalName", header: "Hospital", enableSorting: true },
      { accessorKey: "wardName", header: "Ward", enableSorting: true },
      {
        accessorKey: "signUpDate",
        header: "Signed up",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.signUpDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "firstLoginDate",
        header: "First login",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.firstLoginDate, "Never")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "archivedDate",
        header: "Archived",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.archivedDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastUsedDate",
        header: "Last used",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastUsedDate, "Never")}
          </span>
        ),
        enableSorting: true,
      },
      {
        // Retool computed this in a query transformer; it is presentation over
        // two dates the row already carries, so it stays client-side.
        id: "monitoringLength",
        header: "Monitoring length",
        accessorFn: (patient) =>
          formatMonitoringLength(
            patient.archivedDate,
            patient.firstLoginDate
          ) ?? "-",
        enableSorting: true,
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Archived patients</PageTitle>
          <PageInfoTooltip title="Archived patients">
            Discharged patients, most recently archived first. Their records are
            still editable, but the actions that only make sense for a patient
            still using the app — password reset, enrolment email, changing the
            sign-in address and eMED registration — are unavailable until they
            are un-archived.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <HospitalWardFilter
          idPrefix="admin-archived-patients"
          hospitalId={hospitalId}
          wardId={wardId}
          onHospitalChange={handleHospitalChange}
          onWardChange={handleWardChange}
        />
        <SearchInput
          type="text"
          placeholder="Search by ID, name, email, MRN, ward or hospital..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the archived patient list. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[ARCHIVED_PATIENTS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={patients}
            emptyStateText={
              isFetching ? <TableLoadingState /> : "No rows found"
            }
            onRowClick={(patient) =>
              navigate(`/admin/patients/${patient.patientId}`)
            }
          />
        </TableWrapper>
        <TablePagination
          pagination={{
            currentPage: page,
            pageSize,
            totalPages,
            totalItems: totalCount,
            hasNextPage: page < totalPages,
            hasPreviousPage: page > 1,
          }}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          exportCsv={{
            filename: "archived-patients",
            columns,
            // Server-paginated: only the fetched page can be written, and the
            // button's row count says so.
            rows: patients,
            currentPageOnly: true,
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
